import {
    useLayoutEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    DateSelectorSwapConstantType,
    DateSelectorSwapState
} from "./type";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type MotionStyleType,
    useMotionTiming,
    getSlideStyle
} from "../../helpers/motion";
import {
    getDirectionSign
} from "../../helpers/direction";

export const useDateSelectorSwap = ({
    pageIndex: pageIndexProp,
    customTheme
}: DateSelectorSwapConstantType): MotionStyleType | undefined => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pageIndex = Number.isFinite(pageIndexProp) ? pageIndexProp : 0;

    const {
        isReduceMotionEnabled,
        duration,
        easing
    } = useMotionTiming("contentSwap", customTheme, "fade");

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const progress = useRef(new Animated.Value(1)).current;

    const pageIndexRef = useRef(pageIndex);

    const isRunningRef = useRef(false);

    const [
        swap,
        setSwap
    ] = useState<DateSelectorSwapState>({
        pageIndex,
        step: 1
    });

    const offset = swap.step * getDirectionSign({
        direction
    }) * spaces.spacingSm;

    if(swap.pageIndex !== pageIndex) {
        setSwap({
            step: pageIndex > swap.pageIndex ? 1 : -1,
            pageIndex
        });
    }

    useLayoutEffect(() => {
        if(pageIndexRef.current === pageIndex) {
            return;
        }

        pageIndexRef.current = pageIndex;

        if(!duration) {
            progress.setValue(1);

            isRunningRef.current = false;

            return;
        }

        if(!isRunningRef.current) {
            progress.setValue(0);
        }

        isRunningRef.current = true;

        const animation = Animated.timing(progress, {
            useNativeDriver: Platform.OS !== "web",
            toValue: 1,
            duration,
            easing
        });

        animation.start(({
            finished
        }) => {
            if(finished) {
                isRunningRef.current = false;
            }
        });

        return () => {
            animation.stop();
        };
    }, [
        pageIndex
    ]);

    return useMemo(() => duration ? getSlideStyle({
        isReduceMotionEnabled,
        axis: "x",
        progress,
        offset
    }) : undefined, [
        isReduceMotionEnabled,
        progress,
        duration,
        offset
    ]);
};
